import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useMobileViewport from "hooks/useMobileViewport";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Screen types that can be pushed onto the drawer stack. */
export type DrawerStackScreenType =
  | "issueFeed"
  | "issueInfluences";

/** Common shape every screen carries. */
interface DrawerStackScreenBase {
  id: string;
}

/** Issue feed (drawer over an issue cname). */
export interface IssueFeedDrawerScreen extends DrawerStackScreenBase {
  type: "issueFeed";
  cname: string;
}

/** Issue influences (drawer over an issue cname). */
export interface IssueInfluencesDrawerScreen extends DrawerStackScreenBase {
  type: "issueInfluences";
  cname: string;
}

/** A single entry in the drawer navigation stack. */
export type DrawerStackScreen =
  | IssueFeedDrawerScreen
  | IssueInfluencesDrawerScreen;

/** Screen with `id` stripped — the input shape for `push`. */
export type DrawerStackScreenInput =
  | Omit<IssueFeedDrawerScreen, "id">
  | Omit<IssueInfluencesDrawerScreen, "id">;

interface DrawerStackContextValue {
  /** Current stack of drawer screens (bottom → top). */
  stack: DrawerStackScreen[];
  /** Push a new screen onto the top of the stack. */
  push: (screen: DrawerStackScreenInput) => void;
  /** Remove the topmost screen from the stack. */
  pop: () => void;
  /** Remove all screens from the stack. */
  clear: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const DrawerStackContext = createContext<DrawerStackContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

let _nextId = 0;
const ISSUE_HASH_PREFIX = "#issue-";
const ISSUE_FEED_HASH_SUFFIX = "-feed";
const ISSUE_INFLUENCES_HASH_SUFFIX = "-influences";

/** Monotonically-increasing id generator to keep screen keys stable. */
const generateScreenId = (): string => {
  _nextId += 1;
  return `ds-${_nextId}-${Date.now()}`;
};

/** Build a user-facing hash for a drawer target. */
const buildDrawerHash = (screen: DrawerStackScreenInput): string => {
  const encodedCname = encodeURIComponent(screen.cname);
  return screen.type === "issueInfluences"
    ? `${ISSUE_HASH_PREFIX}${encodedCname}${ISSUE_INFLUENCES_HASH_SUFFIX}`
    : `${ISSUE_HASH_PREFIX}${encodedCname}${ISSUE_FEED_HASH_SUFFIX}`;
};

const decodeHashCname = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

/**
 * Parse a semantic drawer hash into the issue-level shape it represents.
 */
const parseIssueDrawerHash = (
  hash: string,
):
  | Omit<IssueFeedDrawerScreen, "id">
  | Omit<IssueInfluencesDrawerScreen, "id">
  | null => {
  if (!hash.startsWith(ISSUE_HASH_PREFIX)) {
    return null;
  }

  const target = hash.slice(ISSUE_HASH_PREFIX.length);

  if (target.endsWith(ISSUE_INFLUENCES_HASH_SUFFIX)) {
    const encodedCname = target.slice(0, -ISSUE_INFLUENCES_HASH_SUFFIX.length);
    const cname = decodeHashCname(encodedCname);
    return cname ? { type: "issueInfluences", cname } : null;
  }

  if (target.endsWith(ISSUE_FEED_HASH_SUFFIX)) {
    const encodedCname = target.slice(0, -ISSUE_FEED_HASH_SUFFIX.length);
    const cname = decodeHashCname(encodedCname);
    return cname ? { type: "issueFeed", cname } : null;
  }

  return null;
};

const parseDefaultIssueRoute = (
  pathname: string,
): Omit<IssueFeedDrawerScreen, "id"> | null => {
  const defaultMatch = pathname.match(/^\/issue\/([^/]+)\/?$/);
  if (!defaultMatch) {
    return null;
  }

  const cname = decodeHashCname(defaultMatch[1]);
  return cname ? { type: "issueFeed", cname } : null;
};

const buildIssueBasePath = (cname: string): string =>
  `/issue/${encodeURIComponent(cname)}`;

const buildIssueRoutePath = (
  screen:
    | Omit<IssueFeedDrawerScreen, "id">
    | Omit<IssueInfluencesDrawerScreen, "id">,
): string =>
  `${buildIssueBasePath(screen.cname)}/${
    screen.type === "issueInfluences" ? "influences" : "feed"
  }`;

const screenMatches = (
  screen: DrawerStackScreen,
  target: DrawerStackScreenInput,
): boolean => {
  if (screen.type !== target.type) return false;
  if (
    (screen.type === "issueFeed" || screen.type === "issueInfluences") &&
    (target.type === "issueFeed" || target.type === "issueInfluences")
  ) {
    return screen.cname === target.cname;
  }
  return false;
};

const screenMatchesIssueHash = (
  screen: DrawerStackScreen,
  target:
    | Omit<IssueFeedDrawerScreen, "id">
    | Omit<IssueInfluencesDrawerScreen, "id">,
): boolean => {
  return screen.type === target.type && screen.cname === target.cname;
};

const screenWithId = (
  screen: DrawerStackScreenInput,
): DrawerStackScreen => {
  const id = generateScreenId();
  return { ...screen, id };
};

/**
 * Provides the drawer-stack state to its subtree.
 * Must be rendered inside a `<Router>` (uses `useLocation`).
 *
 * Mobile drawers are mirrored into semantic URL hashes (for example
 * `#issue-Peter_Thiel-feed` and `#issue-Peter_Thiel-influences`) so browser
 * and OS back buttons close the top drawer without changing the visible
 * pathname. Desktop clients translate those hashes back to the matching
 * canonical route (`/issue/Peter_Thiel/feed`, `/issue/Peter_Thiel/influences`).
 */
export const DrawerStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<DrawerStackScreen[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useMobileViewport();

  /** Track the pathname so we can clear the stack when the route changes. */
  const prevPathnameRef = useRef(location.pathname);
  const pendingPushHashRef = useRef<string | null>(null);
  const pushedHashStackRef = useRef<string[]>([]);

  useEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      pendingPushHashRef.current = null;
      pushedHashStackRef.current = [];
      setStack([]);
    }
  }, [location.pathname]);

  useEffect(() => {
    const issueHashScreen = parseIssueDrawerHash(location.hash);
    const defaultIssueRoute = parseDefaultIssueRoute(location.pathname);

    if (isMobileViewport) {
      return;
    }

    if (issueHashScreen) {
      navigate(
        {
          pathname: buildIssueRoutePath(issueHashScreen),
          search: location.search,
          hash: "",
        },
        { replace: true },
      );
      return;
    }

    if (defaultIssueRoute) {
      navigate(
        {
          pathname: buildIssueRoutePath(defaultIssueRoute),
          search: location.search,
          hash: "",
        },
        { replace: true },
      );
    }
  }, [
    isMobileViewport,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!isMobileViewport) {
      setStack((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const issueHashScreen = parseIssueDrawerHash(location.hash);

    setStack((prev) => {
      const pendingHash = pendingPushHashRef.current;
      if (pendingHash && pendingHash === location.hash) {
        // The hash we just pushed has landed in the location; record that
        // the corresponding screen is now in the in-memory stack and clear
        // the pending marker.
        pendingPushHashRef.current = null;
        return prev;
      }

      if (!issueHashScreen) {
        pushedHashStackRef.current = [];
        return prev.length === 0 ? prev : [];
      }

      // issueHashScreen is non-null at this point but TS narrowing cannot
      // see through the conditional branches above; guard explicitly.
      if (!issueHashScreen) {
        return prev;
      }
      const matchingIndex = prev.findIndex((screen) =>
        screenMatchesIssueHash(screen, issueHashScreen),
      );
      if (matchingIndex >= 0) {
        pushedHashStackRef.current = pushedHashStackRef.current.slice(0, matchingIndex + 1);
        return matchingIndex === prev.length - 1
          ? prev
          : prev.slice(0, matchingIndex + 1);
      }

      pushedHashStackRef.current = [];
      return [screenWithId(issueHashScreen)];
    });
  }, [isMobileViewport, location.hash, location.pathname]);

  const push = useCallback(
    (screen: DrawerStackScreenInput) => {
      const topScreen = stack[stack.length - 1];
      if (topScreen && screenMatches(topScreen, screen)) {
        return;
      }

      const hash = buildDrawerHash(screen);
      pendingPushHashRef.current = hash;
      pushedHashStackRef.current = [...pushedHashStackRef.current, hash];
      setStack((prev) => [...prev, screenWithId(screen)]);
      navigate({ hash });
    },
    [navigate, stack],
  );

  const pop = useCallback(() => {
    if (stack.length === 0) {
      return;
    }

    const lastPushedHash =
      pushedHashStackRef.current[pushedHashStackRef.current.length - 1];

    if (lastPushedHash === location.hash) {
      pushedHashStackRef.current = pushedHashStackRef.current.slice(0, -1);
      navigate(-1);
      return;
    }

    pushedHashStackRef.current = [];
    setStack((prev) => prev.slice(0, -1));
    navigate({ hash: "" }, { replace: true });
  }, [location.hash, navigate, stack]);

  const clear = useCallback(() => {
    pendingPushHashRef.current = null;
    pushedHashStackRef.current = [];
    setStack([]);
    if (parseIssueDrawerHash(location.hash)) {
      navigate({ hash: "" }, { replace: true });
    }
  }, [location.hash, navigate]);

  const value = useMemo<DrawerStackContextValue>(
    () => ({ stack, push, pop, clear }),
    [stack, push, pop, clear],
  );

  return (
    <DrawerStackContext.Provider value={value}>
      {children}
    </DrawerStackContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Raw access to the drawer-stack context.
 * @throws if used outside a `DrawerStackProvider`.
 */
export const useDrawerStack = (): DrawerStackContextValue => {
  const ctx = useContext(DrawerStackContext);
  if (!ctx) {
    throw new Error("useDrawerStack must be used within a DrawerStackProvider");
  }
  return ctx;
};

/**
 * Platform-aware navigation helpers.
 * On mobile: pushes a new drawer onto the stack.
 * On desktop: navigates via React Router (inline rendering).
 */
export const useDrawerNavigation = () => {
  const { push } = useDrawerStack();
  const navigate = useNavigate();
  const isMobileViewport = useMobileViewport();

  /** Open the IssueFeedSurface for a given issue. */
  const openIssueFeed = useCallback(
    (cname: string) => {
      if (isMobileViewport) {
        push({ type: "issueFeed", cname });
      } else {
        navigate(`/issue/${encodeURIComponent(cname)}/feed`);
      }
    },
    [isMobileViewport, push, navigate],
  );

  /** Open the IssueInfluenceSurface for a given issue. */
  const openIssueInfluences = useCallback(
    (cname: string) => {
      if (isMobileViewport) {
        push({ type: "issueInfluences", cname });
      } else {
        navigate(`/issue/${encodeURIComponent(cname)}/influences`);
      }
    },
    [isMobileViewport, push, navigate],
  );

  /** Navigate to the full IssueProfile (always route-based, never stacked). */
  const openIssueProfile = useCallback(
    (cname: string) => {
      navigate(`/issue/${encodeURIComponent(cname)}`);
    },
    [navigate],
  );

  return { openIssueFeed, openIssueInfluences, openIssueProfile };
};
